import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl } from 'react-native';
import SkeletonRow from '@/components/SkeletonRow';
import { createLogger } from '@/lib/logger';
import EmptyStateInline from '@/components/EmptyStateInline';
import { useState, useEffect } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Notification } from '@/types/database';
import { router } from 'expo-router';
import { Swords, Trophy, Dumbbell, UserPlus, Clock, Bell } from 'lucide-react-native';
import * as Notifications from 'expo-notifications';

const log = createLogger('NOTIFICATIONS');

function getNotificationIcon(type: string) {
  switch (type) {
    case 'duel_challenge':
      return { Icon: Swords, color: '#F97316' }; // orange
    case 'duel_accepted':
      return { Icon: Swords, color: '#22C55E' }; // green
    case 'duel_complete':
      return { Icon: Trophy, color: '#EAB308' }; // gold
    case 'duel_lift':
      return { Icon: Dumbbell, color: '#3B82F6' }; // blue
    case 'new_follower':
      return { Icon: UserPlus, color: '#A855F7' }; // purple
    case 'challenge_reminder':
      return { Icon: Clock, color: '#F59E0B' }; // amber
    default:
      return { Icon: Bell, color: '#9CA3AF' }; // gray
  }
}

function navigateForNotification(notification: Notification) {
  const data = notification.data as any;
  const type = notification.type;

  if (type === 'duel_challenge' || type === 'duel_accepted' || type === 'duel_complete' || type === 'duel_lift') {
    if (data?.duelId) {
      router.push(`/duels/${data.duelId}` as any);
    } else if (data?.duel_id) {
      router.push(`/duels/${data.duel_id}` as any);
    } else {
      router.push('/(tabs)/duels' as any);
    }
  } else if (type === 'new_follower') {
    router.push('/(tabs)/friends' as any);
  } else if (type === 'challenge_reminder') {
    router.push('/(tabs)/home' as any);
  }
}

export default function NotificationsScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, [profile]);

  // Clear iOS badge count when the notifications tab is viewed
  useEffect(() => {
    Notifications.setBadgeCountAsync(0).catch(() => {});
  }, []);

  const loadNotifications = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      log.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadNotifications();
  };

  const handleNotificationPress = async (notification: Notification) => {
    try {
      if (!notification.is_read) {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', notification.id);

        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
      }

      navigateForNotification(notification);
    } catch (error) {
      log.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!profile) return;

    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', profile.id)
        .eq('is_read', false);

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      log.error('Error marking all as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} edges={['top']}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 40 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.text }]} accessibilityRole="header">Notifications</Text>
        {unreadCount > 0 ? (
          <TouchableOpacity onPress={markAllAsRead} accessibilityLabel="Mark all notifications as read">
            <Text style={[styles.markAllRead, { color: theme.colors.accent }]}>
              Mark all read
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 80 }} />
        )}
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={theme.colors.accent}
          />
        }>
        {loading ? (
          <View style={styles.emptyContainer}>
            <SkeletonRow rows={4} />
          </View>
        ) : notifications.length === 0 ? (
          <EmptyStateInline
            title="No notifications yet"
            description="Duel results and updates will appear here."
          />
        ) : (
          notifications.map(notification => {
            const { Icon, color: iconColor } = getNotificationIcon(notification.type);
            return (
              <TouchableOpacity
                key={notification.id}
                style={[
                  styles.notificationCard,
                  {
                    backgroundColor: notification.is_read
                      ? theme.colors.surface
                      : theme.colors.surfaceVariant,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={() => handleNotificationPress(notification)}>
                <View style={styles.notificationRow}>
                  <View style={[styles.iconContainer, { backgroundColor: iconColor + '20' }]}>
                    <Icon size={20} color={iconColor} />
                  </View>
                  <View style={styles.notificationContent}>
                    <View style={styles.notificationHeader}>
                      <Text style={[styles.notificationTitle, { color: theme.colors.text }]} numberOfLines={1}>
                        {notification.title}
                      </Text>
                      {!notification.is_read && <View style={[styles.unreadDot, { backgroundColor: theme.colors.accent }]} />}
                    </View>
                    <Text style={[styles.notificationBody, { color: theme.colors.textSecondary }]} numberOfLines={2}>
                      {notification.body}
                    </Text>
                    <Text style={[styles.notificationTime, { color: theme.colors.textTertiary }]}>
                      {formatTimestamp(notification.created_at)}
                    </Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    marginLeft: 8,
  },
  markAllRead: {
    fontSize: 14,
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  notificationCard: {
    padding: 12,
    borderRadius: 2,
    marginBottom: 12,
    borderWidth: 1,
  },
  notificationRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  notificationContent: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '700',
    flex: 1,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginLeft: 8,
  },
  notificationBody: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 4,
  },
  notificationTime: {
    fontSize: 12,
  },
});
